/** @jsxImportSource @emotion/react */
import { useAuth0 } from '@auth0/auth0-react';
import { Badge, FlexLayout, Text } from '@deity/falcon-ui';
import { Button, Icon, Menu, MenuItem } from '@deity-io/portal-ui';
import { Dashboard } from '@mui/icons-material';
import FlowsBadge from 'components/Header/Badges/FlowsBadge';
import VersionBadge from 'components/Header/Badges/VersionBadge';
import EnvironmentPicker from 'components/PackageVersion/EnvironmentPicker';
import { UIContext } from 'contexts/UIContext';
import { CACHE_FIRST, ENTITY_PATH, ENTITY_PATH_ID } from 'contracts/constants';
import { ContentContainer } from 'layouts';
import type { FC, MouseEvent } from 'react';
import { useContext, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useGetPackageQuery, useGetPackageVersionQuery } from 'services/graphql/generated/hooks';
import { getParams } from 'utils';

export const Header: FC = () => {
  const { logout, user } = useAuth0();
  const ctxUi = useContext(UIContext);
  const { pathname } = useLocation();
  const { packageId, versionUuid } = getParams(pathname, [ENTITY_PATH_ID, ENTITY_PATH]);
  const { data: selectedPackage } = useGetPackageQuery({
    variables: { uuid: packageId, isDeleted: 0 },
    skip: !packageId,
    ...CACHE_FIRST
  });

  const { data: packageVersion } = useGetPackageVersionQuery({
    ...{ variables: { package_uuid: packageId, uuid: versionUuid, isDeleted: 0 }, skip: !versionUuid || !packageId },
    ...CACHE_FIRST
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleClick = (event: MouseEvent<HTMLButtonElement> | undefined) => {
    if (event) {
      setAnchorEl(event.currentTarget);
    }
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const path = () => {
    if (pathname === '/packages') {
      return 'packages';
    }
    if (pathname.split('/')[pathname.split('/').length - 1] === 'versions') {
      return 'versions';
    }

    return undefined;
  };
  const envPickerMemo = useMemo(
    () => (
      <>
        <Text mr="xs" color="secondary">
          {selectedPackage?.getPackage?.name || 'Select Package'}
        </Text>
        {packageVersion && [
          <VersionBadge key="versions" packageVersionType={packageVersion.getPackageVersion} />,
          <EnvironmentPicker key="env" />,
          <FlowsBadge key="flows" />
        ]}
      </>
    ),
    [packageVersion?.getPackageVersion, ctxUi?.state.currentEnvironment]
  );

  return (
    <FlexLayout
      bg="primaryText"
      height="xxl"
      position="fixed"
      width="full"
      style={{ zIndex: 1000 }}
      justifyContent="center"
    >
      <ContentContainer>
        <FlexLayout height="xxl" width="full" justifyContent="space-between">
          <FlexLayout height="xxl" alignItems="center">
            <Link style={{ textDecoration: 'none' }} to="/packages">
              <FlexLayout alignItems="center" color="secondary" mr="xs">
                <Dashboard />
              </FlexLayout>
            </Link>
            {path() ? (
              <Text mr="xs" color="secondary">
                Select Package {path() === 'versions' && 'Version'}
              </Text>
            ) : (
              envPickerMemo
            )}
          </FlexLayout>
          <FlexLayout alignItems="center">
            <Badge variant="account" mr="sm">
              <Text fontSize="xs" color="secondary" css={{ textTransform: 'uppercase' }}>
                Admin
              </Text>
            </Badge>
            <Button
              sx={{ color: 'white' }}
              variant="text"
              endIcon={<Icon icon="arrowDropDown" />}
              onClick={handleClick}
            >
              {user?.name}
            </Button>

            <Menu open={!!anchorEl} anchorEl={anchorEl} onClose={handleClose}>
              <MenuItem
                onClick={() => {
                  handleClose();
                  logout();
                }}
                icon={<Icon icon="logout" />}
              >
                Logout
              </MenuItem>
            </Menu>
          </FlexLayout>
        </FlexLayout>
      </ContentContainer>
    </FlexLayout>
  );
};
